import type { IntlShape } from 'react-intl';
import { EMAIL_SHARE_URL, TWITTER_SHARE_URL } from 'newApp/constants/share';
import { isShobiddak } from 'newApp/helpers/domain';
import { generateOpenSooqRedirectUrl } from 'newApp/helpers/openSooq';
import type { AccountStats, BuyerSummary, SellerSummary } from 'newApp/types/account';
import { SHARE_CAMPAIGN, VIEW_ACCOUNT_CAMPAIGN } from './constants';
import type { BuyerRow, ProductRow, SellerRow, ShareTemplates, StatRow } from './types';

const OPENSOOQ_BASE = process.env.REACT_APP_OPENSOOQ_BASE ?? '';

export const getMainPageTitleId = (): string =>
  isShobiddak() ? 'routes.home.shobiddak' : 'routes.home';

export const buildHelpUrl = (country: string, lang: string): string =>
  `${generateOpenSooqRedirectUrl(OPENSOOQ_BASE, country, lang)}/site/dl?requestUri=${country}/help`;

export const buildAboutUrl = (country: string): string =>
  `${generateOpenSooqRedirectUrl(OPENSOOQ_BASE, country)}/site/about-us`;

export const buildAdvertisingProductsUrl = (country: string, lang: string): string =>
  lang === 'ar'
    ? `https://${country}.opensooq.com/ar/الخدمات-الاعلانية`
    : `https://${country}.opensooq.com/en/advertising-products`;

const buildMemberProfileUrl = (country: string, memberLink: string, campaign: string): string =>
  `${generateOpenSooqRedirectUrl(OPENSOOQ_BASE.replace('/{lang}', ''), country)}${memberLink}` +
  `?utm_source=opensooq&utm_medium=share&utm_campaign=${campaign}`;

export const buildMemberShareUrl = (country: string, memberLink: string): string =>
  buildMemberProfileUrl(country, memberLink, SHARE_CAMPAIGN);

export const buildMemberViewUrl = (country: string, memberLink: string): string =>
  buildMemberProfileUrl(country, memberLink, VIEW_ACCOUNT_CAMPAIGN);

export const buildShareTemplates = (
  intl: IntlShape,
  memberName: string,
  shortUrl: string,
): ShareTemplates => {
  const isArabic = intl.locale.startsWith('ar');
  const prefix = isArabic ? '\u202B' : '';
  const suffix = isArabic ? '\u202C' : '';
  const wrap = (value: string) => `${prefix}${value}${suffix}`;
  const headline = intl.formatMessage({ id: 'shareMyAccount.message' }, { name: memberName });
  const downloadApp = intl.formatMessage({ id: 'downloadApp' });

  const build = (storeUrl: string) =>
    [wrap(headline), ' ', wrap(shortUrl), ' ', wrap(`${downloadApp} ${storeUrl}`)].join('\n');

  return { email: build(EMAIL_SHARE_URL), twitter: build(TWITTER_SHARE_URL) };
};

export const buildAccountStatsRows = (intl: IntlShape, stats?: AccountStats): StatRow[] => [
  {
    dataId: 'home-account-statistics-totalListingsViews',
    title: intl.formatMessage({ id: 'myOpenSooq.totalListingViews' }),
    tone: 'success',
    value: stats?.postCountTotal ?? 0,
  },
  {
    dataId: 'home-account-statistics-totalAccountViews',
    title: intl.formatMessage({ id: 'myOpenSooq.totalAccountViews' }),
    tone: 'primary',
    value: stats?.viewsCountTotal ?? 0,
  },
  {
    dataId: 'home-account-statistics-totalCVViews',
    title: intl.formatMessage({ id: 'myOpenSooq.totalCvViews' }),
    tone: 'neutral',
    value: stats?.cvCountTotal ?? 0,
  },
];

export const buildSellerRows = (intl: IntlShape, seller?: SellerSummary): SellerRow[] => {
  const byStatus = seller?.listing_by_status;

  const rows: SellerRow[] = [
    {
      dataId: 'home-seller-promoted',
      href: '/listings/my-listings?status_featured=1',
      title: intl.formatMessage({ id: 'status.promoted' }),
      tone: 'success',
      value: byStatus?.Live_Promoted ?? 0,
    },
    {
      dataId: 'home-seller-regular',
      href: '/listings/my-listings?status_regular=1',
      title: intl.formatMessage({ id: 'status.regular' }),
      tone: 'success',
      value: byStatus?.Live_Regular ?? 0,
    },
    {
      dataId: 'home-seller-pendingPayment',
      href: '/listings/my-listings?status_payment=1',
      title: intl.formatMessage({ id: 'status.pendingPayment' }),
      tone: 'warning',
      value: byStatus?.Pending_Payment ?? 0,
    },
    {
      dataId: 'home-seller-pendingReview',
      href: '/listings/my-listings?status_review=1',
      title: intl.formatMessage({ id: 'status.pendingReview' }),
      tone: 'warning',
      value: byStatus?.Pending_Review ?? 0,
    },
  ];

  const pendingNafath = byStatus?.Pending_Nafath;

  if (pendingNafath !== undefined && pendingNafath !== null) {
    rows.push({
      dataId: 'home-seller-pendingNafath',
      href: '/listings/my-listings?status_verification=1',
      title: intl.formatMessage({ id: 'status.pendingNafath' }),
      tone: 'warning',
      value: pendingNafath,
    });
  }

  rows.push(
    {
      dataId: 'home-seller-rejected',
      href: '/listings/my-listings?status_rejected=1',
      title: intl.formatMessage({ id: 'status.rejected' }),
      tone: 'danger',
      value: byStatus?.Rejected ?? 0,
    },
    {
      dataId: 'home-seller-deleted',
      href: '/listings/my-listings?status_deleted=1&status_expired=1',
      title: intl.formatMessage({ id: 'status.deletedOrExpired' }),
      tone: 'danger',
      value: byStatus?.Deleted_Expired_Sold ?? 0,
    },
    {
      dataId: 'home-seller-drafts',
      href: '/listings/my-listings/draft-listings',
      title: intl.formatMessage({ id: 'common.drafts' }),
      tone: 'primary',
      value: byStatus?.Drafts ?? 0,
    },
  );

  return rows;
};

export const buildBuyerRows = (
  intl: IntlShape,
  buyer: BuyerSummary | undefined,
  recentlyViewedCount: number,
): BuyerRow[] => [
  {
    dataId: 'home-buyer-favoriteListings',
    href: '/listings/demand/favorite-listings',
    icon: 'favorites',
    title: intl.formatMessage({ id: 'myOpenSooq.favoriteListing' }),
    value: buyer?.favorite_listings.total ?? 0,
  },
  {
    dataId: 'home-buyer-recentlyViewed',
    href: '/listings/demand/recently-viewed',
    icon: 'recently-viewed',
    title: intl.formatMessage({ id: 'myOpenSooq.recentlyViewed' }),
    value: recentlyViewedCount,
  },
  {
    dataId: 'home-buyer-followingListings',
    href: '/listings/demand/following-listings',
    icon: 'following',
    title: intl.formatMessage({ id: 'myOpenSooq.followingListing' }),
    value: buyer?.followings_listings.total ?? 0,
  },
  {
    dataId: 'home-buyer-jobApplications',
    href: '/listings/demand/job-applications',
    icon: 'jobs',
    title: intl.formatMessage({ id: 'myOpenSooq.jobApplication' }),
    value: buyer?.job_applications.total ?? 0,
  },
  {
    dataId: 'home-buyer-savedSearches',
    href: '/listings/searches/saved-search',
    icon: 'saved-search',
    title: intl.formatMessage({ id: 'myOpenSooq.savedSearch' }),
    value: buyer?.recent_saved_search.total_saved ?? 0,
  },
  {
    dataId: 'home-buyer-recentSearches',
    href: '/listings/searches/recent-search',
    icon: 'search',
    title: intl.formatMessage({ id: 'myOpenSooq.recentSearch' }),
    value: buyer?.recent_saved_search.total_recent ?? 0,
  },
];

export const buildProductRows = (intl: IntlShape, advertisingUrl: string): ProductRow[] => [
  {
    dataId: 'home-products-carHistoryReport',
    href: '/services/carfax-report',
    title: intl.formatMessage({ id: 'myOpenSooq.products.text2' }),
  },
  {
    dataId: 'home-products-hideAds',
    href: '/products',
    title: intl.formatMessage({ id: 'myOpenSooq.products.text3' }),
  },
  {
    dataId: 'home-products-moreViews',
    externalHref: advertisingUrl,
    title: intl.formatMessage({ id: 'myOpenSooq.products.text4' }),
  },
];
